import React, { useEffect, useState } from 'react';
import css from './TalkAbout.module.css';
import { useHiddenListingIds, withoutHidden } from '../../util/hiddenListings';
import { useDispatch } from 'react-redux';

import { denormalisedResponseEntities } from '../../util/data';
import usePresenceMap from '../../util/usePresenceMap';
import { rankListingsByOverlap, topicsFromListing } from '../../util/sessionTopics';
import { rankListingsByPaths } from '../../util/topicPaths';
import { MatchRow } from './TalkAbout';

/**
 * Talker dashboard: fetch published listener listings and show the best
 * interest overlap. Talker interests stay private (used here only).
 */
export const useListenerListings = () => {
  const dispatch = useDispatch();
  const [rawListings, setListings] = useState([]);
  let listings = rawListings;
  useEffect(() => {
    let alive = true;
    // Sam Oct 26: page through every published listing (was capped at 60).
    dispatch((d, getState, sdk) => {
      const q = page =>
        sdk.listings.query({
          page,
          perPage: 100,
          include: ['author', 'images'],
          'fields.image': ['variants.listing-card', 'variants.square-small2x'],
        });
      const all = [];
      const next = page =>
        q(page).then(res => {
          all.push(...denormalisedResponseEntities(res));
          const total = res?.data?.meta?.totalPages || 1;
          if (alive && page === 1) setListings(all.slice());
          return page < total && page < 20 ? next(page + 1) : null;
        });
      return next(1)
        .then(() => {
          if (alive) setListings(all.slice());
        })
        .catch(() => {
          if (alive && all.length) setListings(all.slice());
        });
    });
    return () => {
      alive = false;
    };
  }, [dispatch]);
  const hiddenIds = useHiddenListingIds();
  listings = withoutHidden(listings, hiddenIds);
  const ids = listings.map(l => l?.id?.uuid).filter(Boolean);
  const { map: presence = {}, busyMap = {}, pausedMap = {}, liveMap = {} } = usePresenceMap(ids, {
    includeAllOnline: false,
  });
  // Oct 27 2026 (Sam): Available now = the Available flag (publicData.availableNow) or a live
  // presence; no heartbeat required. Still excludes in-session and Start-now-paused listeners.
  const flagOn = Object.fromEntries(
    listings
      .filter(l => l?.id?.uuid && l?.attributes?.publicData?.availableNow === true)
      .map(l => [l.id.uuid, true])
  );
  const available = Object.fromEntries(
    ids
      .filter(id => presence[id] && !busyMap[id] && !pausedMap[id]) // Oct 29 lock: live only (flagOn = Texts you)
      .map(id => [id, true])
  );
  void liveMap; void flagOn; // Oct 29 2026: presence map is live-only now.
  const away = Object.fromEntries(
    listings
      .filter(l => {
        const id = l?.id?.uuid;
        const on = presence[id] || l?.attributes?.publicData?.availableNow === true;
        return id && on && !available[id] && !busyMap[id];
      })
      .map(l => [l.id.uuid, true])
  );
  return { listings, presence: available, away };
};

const TalkerMatchRow = ({ talkerTopics, talkerPaths }) => {
  const { listings, presence } = useListenerListings();
  const rows = rankListingsByPaths(listings, talkerPaths, presence, 8, () =>
    rankListingsByOverlap(listings, talkerTopics, presence, 8)
  );
  return <MatchRow rows={rows} />;
};

/** Listeners for one topic/interest, Available Now first. */
export const TopicListeners = ({ topicId, title }) => {
  const { listings, presence } = useListenerListings();
  const rows = rankListingsByOverlap(listings, [topicId], presence, 12);
  return rows.length ? (
    <MatchRow rows={rows} title={title} sub="Available now first. Tap a listener to see their profile." />
  ) : null;
};

export default TalkerMatchRow;

/** Listener → talker prompt (Sam, Oct 15): "Your turn to talk", bookable listeners only, never yourself. */
export const ListenerBookPrompt = ({ listenerTopics, ownListingId, ownUserId }) => {
  const { listings, presence } = useListenerListings();
  const others = listings.filter(
    l => l?.id?.uuid !== ownListingId && (!ownUserId || l?.author?.id?.uuid !== ownUserId)
  );
  const rows = rankListingsByOverlap(others, listenerTopics, presence, 8);
  return (
    <section className={css.yourTurn} data-your-turn="true">
      <MatchRow
        rows={rows}
        title="Your turn to talk"
        sub="You listen to others all day. Book 15 minutes with someone who loves the same things you do. From $10."
      />
      {!rows.length ? (
        <div className={css.yourTurnEmpty}>
          <h2 className={css.yourTurnTitle}>Your turn to talk</h2>
          <p>You listen to others all day. Book 15 minutes with someone who loves the same things you do. From $10.</p>
        </div>
      ) : null}
      <a className={css.yourTurnBtn} href="/s?mode=topics" data-your-turn-cta="true">
        Find someone to talk to →
      </a>
    </section>
  );
};

/**
 * "Other listeners you might like" on every listener profile (Sam, Oct 15): up to 6 compact cards,
 * shared topics first, then Available now. Never the profile owner or the viewer.
 */
export const OtherListeners = ({ listing, viewerId }) => {
  const { listings, presence } = useListenerListings();
  const ownerId = listing?.author?.id?.uuid;
  const listingId = listing?.id?.uuid;
  const pool = listings.filter(l => {
    const a = l?.author?.id?.uuid;
    return l?.id?.uuid !== listingId && (!ownerId || a !== ownerId) && (!viewerId || a !== viewerId);
  });
  const topics = topicsFromListing(listing);
  const ranked = rankListingsByOverlap(pool, topics, presence, 6);
  const used = new Set(ranked.map(r => r.listing.id.uuid));
  const fill = pool
    .filter(l => !used.has(l.id.uuid))
    .map((l, index) => ({ listing: l, score: 0, shared: [], online: !!presence[l.id.uuid], index }))
    .sort((a, b) => Number(b.online) - Number(a.online) || a.index - b.index);
  const rows = [...ranked, ...fill].slice(0, 6);
  if (!rows.length) return null;
  return (
    <section className={css.otherListeners} data-other-listeners={rows.length}>
      <MatchRow rows={rows} title="Other people to talk to" sub="Listeners who talk about similar things, available now first." />
      <a className={css.otherListenersLink} href="/s?mode=listeners" data-other-listeners-all="true">
        See all listeners →
      </a>
    </section>
  );
};
